import { CallCenterAgentState, CallCenterBucket, QueueItem } from '@/api/callcenter/dashboard';
import { ModelID } from '@/types';
import { dateFromApiDateTime } from '@/utils/format';

export type SlaDisplay =
  | { kind: 'countdown'; secondsLeft: number }
  | { kind: 'paused' } // off-hours: "Clock paused"
  | { kind: 'not_started' } // holder is on a call: the accept clock hasn't begun
  | { kind: 'overdue' }
  | { kind: 'held' }; // externally held by the local owner

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

// REST datetimes are Phoenix-naive "Y-m-d H:i:s" strings -- delegate those to
// dateFromApiDateTime so the same timezone-offset math is used everywhere.
// Broadcast/echo payloads are already ISO-8601 UTC (they contain 'T'), so a
// plain `new Date` is correct and delegating them to dateFromApiDateTime would
// double-apply a Phoenix offset that was never there.
export const parseApiOrIsoDate = (value: string | null): Date | null => {
  if (!value) return null;

  const parsed = value.includes('T') ? new Date(value) : dateFromApiDateTime(value);

  return isValidDate(parsed) ? parsed : null;
};

export const deriveSlaDisplay = (
  item: Pick<QueueItem, 'seconds_remaining' | 'sla_deadline_at' | 'externally_held'>,
  elapsedSeconds: number,
  now: Date = new Date(),
  holderOnCall = false,
): SlaDisplay => {
  if (item.externally_held) {
    return { kind: 'held' };
  }

  if (item.seconds_remaining === null) {
    // seconds_remaining is null while paused (off-hours), once already
    // breached, AND while the accept clock hasn't started -- a null
    // sla_deadline_at means the server is waiting for the holder to come off
    // their call before starting the countdown. The holder's own on-call
    // state tells "not started" apart from "off-hours paused";
    // sla_deadline_at is used ONLY as a breach boolean, never as a source to
    // compute a live countdown from.
    const deadline = parseApiOrIsoDate(item.sla_deadline_at);
    const alreadyBreached = deadline !== null && deadline.getTime() < now.getTime();

    if (alreadyBreached) {
      // An overdue offer can now legitimately persist: rotation waits until
      // another agent can actually receive the lead. Overdue means "work
      // this next", not "about to vanish".
      return { kind: 'overdue' };
    }

    if (deadline === null && holderOnCall) {
      return { kind: 'not_started' };
    }

    return { kind: 'paused' };
  }

  const secondsLeft = item.seconds_remaining - elapsedSeconds;

  if (secondsLeft <= 0) {
    return { kind: 'overdue' };
  }

  return { kind: 'countdown', secondsLeft };
};

export type IneligibilityNotice = {
  tone: 'info' | 'warning';
  title: string;
  body?: string;
  showResume?: boolean; // true only for the feed-paused case
};

// Precedence matters and is checked top to bottom -- the first matching
// condition wins. This deliberately does NO staleness math or server-config
// threshold checks (e.g. "how many seconds since the last heartbeat is too
// many"): `eligible` and `last_heartbeat_at` are both already-computed
// signals from the server by the time they reach here, so this function only
// describes WHY the agent is ineligible, not whether one of its inputs looks
// "old enough" to matter.
export const describeIneligibility = (agent: CallCenterAgentState): IneligibilityNotice | null => {
  if (agent.eligible) {
    return null;
  }

  if (agent.feed_paused) {
    return {
      tone: 'info',
      title: 'Your feed is paused',
      body: "You won't receive new leads until you resume.",
      showResume: true,
    };
  }

  if (!agent.bp_is_logged) {
    return { tone: 'warning', title: 'Log in to BrightPattern to receive leads' };
  }

  if (agent.last_heartbeat_at === null) {
    return { tone: 'info', title: 'Connecting your dashboard…' };
  }

  if (agent.bp_state) {
    return {
      tone: 'warning',
      title: agent.bp_reason ? `${agent.bp_state} — ${agent.bp_reason}` : agent.bp_state,
      body: 'Set yourself Ready in BrightPattern to receive leads.',
    };
  }

  return { tone: 'warning', title: "You're not receiving new leads right now" };
};

// Shape of the realtime `callcenter.lead-assigned` / `callcenter.lead-rotated`
// / `callcenter.nudge` broadcast events -- a hand-picked subset of assignment
// fields (see the API repo's broadcast event classes), deliberately thinner
// than QueueItem. broadcastToQueueItem() below bridges the gap so an
// optimistic prepend can reuse QueueList/QueueCard's existing rendering.
export type AssignmentBroadcastPayload = {
  assignment_id: number;
  user_id: number;
  state: string;
  bucket: CallCenterBucket;
  rotation_count: number;
  assigned_at: string | null;
  sla_deadline_at: string | null;
  reason: string | null;
  nudge_type?: 'accept_expiring' | 'idle' | 'sla_warning';
  replacement_user_id?: number | null;
  lead: {
    lead_id: number;
    name: string;
    first_name: string;
    last_name: string;
    phone: string;
    city: string | null;
    state: string | null;
    status: string;
    franchise_id: number;
    waiting_since: string | null;
    created_at: string | null;
  } | null;
};

// Fills in whatever the broadcast payload doesn't carry with the same
// "unknown yet" placeholders a freshly-offered lead would have before its
// first `GET /dashboard` refetch supplies the real values: no dial info yet,
// never externally held (that's a REST-only concept the broadcast doesn't
// send), and no live SLA countdown until the follow-up invalidation lands.
export const broadcastToQueueItem = (payload: AssignmentBroadcastPayload): QueueItem => ({
  assignment_id: payload.assignment_id,
  state: payload.state === 'accepted' ? 'accepted' : 'offered',
  bucket: payload.bucket,
  rotation_count: payload.rotation_count,
  assigned_at: payload.assigned_at,
  accepted_at: null,
  sla_deadline_at: payload.sla_deadline_at,
  seconds_remaining: null,
  externally_held: false,
  dial: null,
  lead: payload.lead
    ? {
        lead_id: payload.lead.lead_id,
        name: payload.lead.name,
        first_name: payload.lead.first_name,
        last_name: payload.lead.last_name,
        phone: payload.lead.phone,
        city: payload.lead.city,
        state: payload.lead.state,
        status: payload.lead.status,
        franchise_id: payload.lead.franchise_id,
        waiting_since: payload.lead.waiting_since,
        followup_at: null,
        created_at: payload.lead.created_at ?? '',
        // Triage detail isn't in the broadcast payload; the invalidation that
        // follows this optimistic prepend fills it in.
        organization_name: null,
        email: null,
        zipcode: null,
        event_type_id: null,
        event_type_name: null,
        date_wanted: null,
        length_wanted: null,
        activity_interest: null,
        total: null,
        special_instructions: null,
        latest_message: null,
        latest_note: null,
        latest_note_at: null,
        updated_at: null,
      }
    : null,
});

export const formatDurationSeconds = (totalSeconds: number): string => {
  const clamped = Math.max(0, Math.floor(totalSeconds));

  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = clamped % 60;
  const paddedSeconds = String(seconds).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
  }

  return `${minutes}:${paddedSeconds}`;
};

// The queue arrives in engine priority order (bucket, deadline, age), so the
// next assignment is simply the first one that isn't the lead being excluded.
// A null exclusion means "nothing to skip" -- the caller is not sitting on a
// lead, so the head of the queue is the answer.
//
// Callers that are about to send the agent to this lead want the whole item,
// not just its id: opening a lead has to accept the assignment, which needs
// the assignment_id.
//
// Externally held items are skipped rather than returned and refused later.
// A lead the local franchise owner is working is not ours to claim, and
// stopping on one would strand the agent on a dead button while a workable
// lead sat further down the queue.
export const pickNextAssignment = (
  queue: QueueItem[],
  excludeLeadId: ModelID | null,
): QueueItem | null =>
  queue.find(
    (item) => item.lead && !item.externally_held && item.lead.lead_id !== Number(excludeLeadId),
  ) ?? null;

export const pickNextAssignedLeadId = (
  queue: QueueItem[],
  excludeLeadId: ModelID | null,
): number | null => pickNextAssignment(queue, excludeLeadId)?.lead?.lead_id ?? null;

// The lead already on screen, so "next" means the one after it. Read from the
// path rather than passed in: the header sits above every route and has no
// other way to know what the agent is looking at.
export const currentLeadIdFromPath = (pathname: string): number | null => {
  const match = pathname.match(/\/leads\/lead\/(\d+)/);

  return match ? Number(match[1]) : null;
};
